import React, { useMemo, useState, useCallback } from 'react';
import InfoIcon from '@atlaskit/icon/glyph/info';
import { getContactFullName, getEmail } from '../../../components/entitlement-page-utils';
import { useQuery } from '@apollo/client';
import { fetchContactsForEntitlement } from '../../../queries/fetch-entitlement-contact-tab';
import { SiteAdmin } from '@css-xp/graphtypes';
import { EntitlementDetails } from '@css-xp/graphtypes/src/generated-types-new';
import { CONTACT_PATH_2 } from '@atlassian/cssxp-typescommon';
import { Link } from 'react-router-dom';
import Avatar from '@atlaskit/avatar';
import Tooltip from '@atlaskit/tooltip';
import CheckIcon from '@atlaskit/icon/glyph/check-circle';
import {
  FormattedDate,
  DynamicResultTable,
  commonContexts,
  CopyableText,
  NoDataContentInTable,
  useFeatureToggle,
} from '@css-xp/base';
import { DEFAULT_ROWS_PER_PAGE, DEFAULT_PAGE, NO_DETAILS_TO_SHOW } from '@atlassian/cssxp-utils-entitlement';
import './contacts-tab.scss';
import { ENABLE_AAID_FOR_CONTACT_PAGE_REDIRECTION } from '@atlassian/cssxp-utils-generic';
import memoize from 'lodash/memoize';

const getContactName = memoize(
  (user?: SiteAdmin | null) => {
    return user?.displayName ?? getEmail(user?.primaryEmail);
  },
  (...args) => {
    return JSON.stringify(args);
  }
);

const ContactsTable = ({ entitlement }: { entitlement: EntitlementDetails }) => {
  const getEntitlementID = () => {
    if (entitlement?.parentSen) {
      return entitlement?.parentSen;
    }
    return entitlement.id;
  };
  const headData = useMemo(() => {
    const cells = [
      {
        content: 'Name',
        width: 30,
      },
      {
        content: 'Email',
        width: 30,
      },
      {
        content: (
          <>
            <Tooltip content="Includes site admins and org admins" tag="span">
              <InfoIcon label="info" size="small" />
            </Tooltip>
            <span className="info-heading-text">Billing user</span>
          </>
        ),
        width: 30,
      },
      {
        content: 'Last logged in',
        width: 30,
      },
    ];

    return {
      cells,
    };
  }, []);

  const emptyStateOrderProp = {
    header: '',
    description: NO_DETAILS_TO_SHOW,
  };
  const [rowsPerPage, setRowsPerPage] = useState<number>(DEFAULT_ROWS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState<number>(DEFAULT_PAGE);
  const [isEnabled] = useFeatureToggle();
  const { data, loading, refetch } = useQuery(fetchContactsForEntitlement, {
    variables: {
      id: getEntitlementID(),
      searchOptions: { first: rowsPerPage, offset: 0 },
    },
    displayName: `fetchStatuspageSiteAdmins`,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    skip: !getEntitlementID(),
  });
  const { loading: isTransactionAccountLoading, getTransactionAccount, data: billingData } = React.useContext(
    commonContexts.TransactionAccountContext
  );
  const onPageChange = useCallback(
    (newPage: number) => {
      setCurrentPage(newPage);
      refetch({
        searchOptions: {
          first: rowsPerPage,
          offset: newPage !== 1 ? newPage * rowsPerPage - rowsPerPage : 0,
        },
      });
    },
    [rowsPerPage, refetch]
  );

  const handleRowsChange = useCallback((rowsCount: number) => {
    setRowsPerPage(rowsCount);
    setCurrentPage(1);
  }, []);

  const transactionAccount = getTransactionAccount(billingData);

  const userTotalCount = data?.entitlementDetails.users?.totalCount ?? 0;
  const siteNodes = data?.entitlementDetails.siteAdmins?.nodes ?? [];
  const copySiteNodes = siteNodes.map((siteNode: any) => {
    return { ...siteNode };
  });
  const userNodes = data?.entitlementDetails.users?.nodes ?? [];
  let copyUserNodes = userNodes.map((userNode: any) => {
    return { ...userNode };
  });

  const billAdminNodes = transactionAccount?.billingAdmins?.nodes ?? [];
  const totalCount = userTotalCount ? userTotalCount : billAdminNodes.length;
  const modifiedBillAdmin = billAdminNodes.map((formatData: any) => {
    const modifiedBill: any = new Object();
    modifiedBill['roles'] = ['BILL_ADMIN'];
    modifiedBill['primaryEmail'] = formatData.email;
    modifiedBill['displayName'] = formatData.fullName;
    modifiedBill['atlassianAccount'] = new Object({ id: formatData.atlassianAccountId });
    return modifiedBill;
  });

  const billingNode = [...modifiedBillAdmin, ...copySiteNodes];
  billingNode.forEach((userInfo: any) => {
    const dupInfo = copyUserNodes.filter((siteInfo: any) => siteInfo.primaryEmail === userInfo.primaryEmail);
    if (dupInfo.length) {
      dupInfo[0].roles = dupInfo[0].roles.concat(userInfo.roles);
    }
  });
  if (!copyUserNodes.length) {
    if (modifiedBillAdmin.length) {
      copyUserNodes = [...modifiedBillAdmin];
    }
  }
  const createRows = (): Array<any> => {
    const result = (copyUserNodes ?? []).map((siteAdmin: SiteAdmin & { atlassianAccount?: { id?: string } }) => {
      const { displayName, primaryEmail, lastActive, roles = [], atlassianAccount } = siteAdmin;
      const email = getEmail(primaryEmail);

      return {
        cells: [
          {
            content: (
              <>
                <Avatar src={undefined} size="small" />
                <Link
                  to={`${CONTACT_PATH_2}/${
                    isEnabled(ENABLE_AAID_FOR_CONTACT_PAGE_REDIRECTION) && atlassianAccount?.id
                      ? atlassianAccount.id
                      : window.btoa(email !== 'N/A' ? email : '')
                  }`}
                  data-testid="contact-link"
                  onClick={(e) => e.stopPropagation()}
                  style={{ verticalAlign: 'super' }}
                  title={getContactFullName(displayName)}
                >
                  {getContactName(siteAdmin)}
                </Link>
              </>
            ),
          },
          {
            content: (
              <span>
                {' '}
                <CopyableText text={email} />
              </span>
            ),
          },
          {
            content: (
              <>
                {
                  //@ts-ignore
                  roles && roles.includes('SITE_ADMIN') ? (
                    <Tooltip content="Can manage basic billing details and has billing ownership of their subscription">
                      <span aria-label="Yes">
                        <CheckIcon aria-label="Yes" label="" size="medium" testId="icon-billing-user" />
                      </span>
                    </Tooltip>
                  ) : (
                    <NoDataContentInTable ariaLabel="No" />
                  )
                }
              </>
            ),
          },
          {
            content: (
              <span>
                {' '}
                {lastActive ? <FormattedDate date={lastActive} showTime={true} /> : <NoDataContentInTable />}
              </span>
            ),
          },
        ],
      };
    });
    return result;
  };

  return (
    <DynamicResultTable
      className="contacts-table"
      testId="contacts-tab"
      headingText="Contacts"
      head={headData}
      rows={createRows()}
      emptyStateProps={emptyStateOrderProp}
      isLoading={loading || isTransactionAccountLoading}
      totalCount={totalCount}
      currentPage={currentPage}
      rowsPerPage={rowsPerPage}
      onPageChange={onPageChange}
      isPaginationEnabled={totalCount > DEFAULT_ROWS_PER_PAGE}
      onRowChange={handleRowsChange}
    />
  );
};

export default ContactsTable;
